/**
 * OnboardingStepShell — shared layout for Steps 1-6 of the onboarding flow.
 *
 * Wraps screen content with:
 *  - Top header: "Kembali" (left, optional) and "Lewati" (right, optional)
 *  - Progress bar (0..1) with step label
 *  - Middle content area (flex: 1)
 *  - Bottom CTA: "Lanjut" button (optional, can be disabled)
 *
 * Step 0 (story) does NOT use this shell — it's a full-screen intro.
 */

import { type ReactNode } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { Brand, Radii, Spacing } from '@/constants/theme';

import { ProgressBar } from './ProgressBar';

type OnboardingStepShellProps = {
  /** Progress as fraction in [0, 1]. */
  progress: number;
  /** Right-aligned step counter, e.g. "2 / 6". */
  stepLabel: string;
  /** Tap handler for the "Kembali" button. Omit to hide. */
  onKembali?: () => void;
  /** Tap handler for the "Lewati" button. Omit to hide. */
  onLewati?: () => void;
  /** Tap handler for the bottom "Lanjut" button. Omit to hide the CTA. */
  onLanjut?: () => void;
  /** CTA label. Defaults to "Lanjut". */
  lanjutLabel?: string;
  /** When true, CTA is non-interactive and visually muted. */
  lanjutDisabled?: boolean;
  /** Middle content area children. */
  children: ReactNode;
  /** Optional style override for the content area. */
  contentStyle?: ViewStyle;
};

export function OnboardingStepShell({
  progress,
  stepLabel,
  onKembali,
  onLewati,
  onLanjut,
  lanjutLabel = 'Lanjut',
  lanjutDisabled = false,
  children,
  contentStyle,
}: OnboardingStepShellProps) {
  return (
    <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
      <View style={styles.header}>
        <HeaderSlot onPress={onKembali} text="‹ Kembali" align="left" />
        <HeaderSlot onPress={onLewati} text="Lewati ›" align="right" />
      </View>

      <View style={styles.progressWrap}>
        <ProgressBar progress={progress} label={stepLabel} />
      </View>

      <View style={[styles.content, contentStyle]}>{children}</View>

      {onLanjut ? (
        <View style={styles.footer}>
          <Pressable
            onPress={onLanjut}
            disabled={lanjutDisabled}
            accessibilityRole="button"
            accessibilityState={{ disabled: lanjutDisabled }}
            style={[styles.cta, lanjutDisabled && styles.ctaDisabled]}
          >
            <ThemedText type="default" style={styles.ctaText}>
              {lanjutLabel}
            </ThemedText>
          </Pressable>
        </View>
      ) : null}
    </SafeAreaView>
  );
}

type HeaderSlotProps = {
  onPress?: () => void;
  text: string;
  align: 'left' | 'right';
};

function HeaderSlot({ onPress, text, align }: HeaderSlotProps) {
  if (!onPress) {
    return <View style={styles.headerSlot} />;
  }
  return (
    <Pressable
      onPress={onPress}
      hitSlop={12}
      accessibilityRole="button"
      style={[styles.headerSlot, align === 'right' && styles.headerSlotRight]}
    >
      <ThemedText type="linkPrimary" style={styles.headerText}>
        {text}
      </ThemedText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.one,
    minHeight: 44,
  },
  headerSlot: {
    minWidth: 88,
    paddingVertical: Spacing.one,
  },
  headerSlotRight: {
    alignItems: 'flex-end',
  },
  headerText: {
    fontSize: 14,
    fontWeight: '500',
  },
  progressWrap: {
    paddingHorizontal: Spacing.three,
    paddingBottom: Spacing.three,
  },
  content: {
    flex: 1,
    paddingHorizontal: Spacing.four,
  },
  footer: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.four,
  },
  cta: {
    backgroundColor: Brand.primary,
    paddingVertical: Spacing.three,
    paddingHorizontal: Spacing.four,
    borderRadius: Radii.medium,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 4,
  },
  ctaDisabled: {
    backgroundColor: '#d1d1d1',
    shadowOpacity: 0,
    elevation: 0,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});
